import React, { useEffect, useState } from 'react';
import {
    Grid,
    Typography,
    TextField,
    Button,
    Dialog,
    DialogContent,
    MenuItem,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import AppDialogTitle from 'components/ui/appDialogTitle';
import { isLocalDev } from 'utils/environment';
import { ContactLensConfigurationService, ValidDataService } from 'service/validDataService';
import validLookup from 'utils/validLookup';
import useLensTypeCatalog from 'hooks/useLensTypeCatalog';

export const initialValuesModelForm = {
    manufacturerId: '',
    materialId: '',
    lensType: '',
    modelName: '',
    uvProtection: false,
    status: validLookup.validStatus.active,
    isTrialAvailable: false,
    trialQuantity: '',
    notes: '',
};

const validationSchema = Yup.object({
    manufacturerId: Yup.number().required('Manufacturer is required').typeError('Manufacturer is required'),
    modelName: Yup.string().required('Model name is required'),
    lensType: Yup.string().required('Lens type is required'),
    trialQuantity: Yup.number().nullable().min(0, 'Trial quantity must be >= 0')
});

export default function ContactLensModelInputForm({ open, onClose, onRecordAdded }) {
    const { contactLensTypes } = useLensTypeCatalog();
    const [manufacturers, setManufacturers] = useState([]);
    const [materials, setMaterials] = useState([]);
    const [loadingData, setLoadingData] = useState(false);
    const [addMfgOpen, setAddMfgOpen] = useState(false);
    const [addMfgSubmitting, setAddMfgSubmitting] = useState(false);
    const [newMfgName, setNewMfgName] = useState('');

    useEffect(() => {
        if (open) {
            loadData();
        }
    }, [open]);

    const fetchManufacturers = async () => {
        const response = await ValidDataService.getManufacturersByType('contact_lens');
        if (response.code === 0) {
            setManufacturers(response.data || []);
        }
    };

    const loadData = async () => {
        setLoadingData(true);
        try {
            const [manufacturerResponse, materialResponse] = await Promise.all([
                ValidDataService.getManufacturersByType('contact_lens'),
                ContactLensConfigurationService.getActiveMaterials()
            ]);

            if (manufacturerResponse.code === 0) {
                setManufacturers(manufacturerResponse.data || []);
            }
            if (materialResponse.code === 0) {
                setMaterials(materialResponse.data || []);
            }
        } catch (error) {
            if (isLocalDev) {
                console.error(error);
            }
            toast.error('Failed to load manufacturers and materials');
        }
        setLoadingData(false);
    };

    const handleSubmit = async (values, { resetForm, setSubmitting }) => {
        try {
            const response = await ContactLensConfigurationService.addModel(values);
            const { code, data, message } = response;

            setSubmitting(false);

            if (code === 0) {
                toast.success(message);
                resetForm();
                onRecordAdded(data);
                onClose();
            } else {
                toast.error(message);
            }
        } catch (error) {
            if (isLocalDev) {
                console.error(error);
            }
            setSubmitting(false);
            toast.error('Failed to add contact lens model');
        }
    };

    return (
        <Dialog open={open} maxWidth={'sm'} fullWidth>
            <AppDialogTitle title={"Add Contact Lens Model"} onClose={onClose} />
            <DialogContent sx={{ mt: 1, mb: 1 }}>
                <Formik
                    initialValues={initialValuesModelForm}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting, setFieldValue, values }) => (
                        <Form>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="manufacturerId"
                                        label="Manufacturer"
                                        required
                                        fullWidth
                                        select
                                        size="small"
                                        disabled={loadingData}
                                    >
                                        <MenuItem value="">-- Select Manufacturer --</MenuItem>
                                        {manufacturers.map((mfg) => (
                                            <MenuItem key={mfg.id} value={mfg.id}>
                                                {mfg.name}
                                            </MenuItem>
                                        ))}
                                    </Field>
                                    <Button
                                        size="small"
                                        sx={{ mt: 0.5, textTransform: 'none' }}
                                        onClick={() => setAddMfgOpen(true)}
                                    >
                                        Don’t see it? Add manufacturer
                                    </Button>
                                    <ErrorMessage name="manufacturerId">{message => <Typography color={'error'}>{message}</Typography>}</ErrorMessage>
                                </Grid>

                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="modelName"
                                        label="Model Name"
                                        required
                                        fullWidth
                                        size="small"
                                        helperText="e.g., Acuvue Oasys, Air Optix Aqua"
                                    />
                                    <ErrorMessage name="modelName">{message => <Typography color={'error'}>{message}</Typography>}</ErrorMessage>
                                </Grid>

                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="lensType"
                                        label="Lens Type"
                                        required
                                        fullWidth
                                        select
                                        size="small"
                                    >
                                        <MenuItem value="">-- Select Lens Type --</MenuItem>
                                        {contactLensTypes.map((type) => (
                                            <MenuItem key={type.key} value={type.key}>{type.label}</MenuItem>
                                        ))}
                                    </Field>
                                    <ErrorMessage name="lensType">{message => <Typography color={'error'}>{message}</Typography>}</ErrorMessage>
                                </Grid>

                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="materialId"
                                        label="Material"
                                        fullWidth
                                        select
                                        size="small"
                                        disabled={loadingData}
                                    >
                                        <MenuItem value="">-- Select Material (Optional) --</MenuItem>
                                        {materials.map((mat) => (
                                            <MenuItem key={mat.id} value={mat.id}>
                                                {mat.name} ({mat.waterContent}% water, Dk {mat.oxygenPermeability})
                                            </MenuItem>
                                        ))}
                                    </Field>
                                </Grid>

                                {/* <Grid item xs={12}>
                                    <Field
                                        as={FormControlLabel}
                                        control={<Checkbox name="uvProtection" />}
                                        label="UV Protection"
                                        name="uvProtection"
                                    />
                                </Grid> */}

                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name='status'
                                        label="Status"
                                        fullWidth
                                        select
                                        size="small"
                                    >
                                        <MenuItem value={"active"}>Active</MenuItem>
                                        <MenuItem value={"inactive"}>Inactive</MenuItem>
                                    </Field>
                                </Grid>

                                <Grid item xs={12}>
                                    <Field name="isTrialAvailable">
                                        {({ field, form }) => (
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        {...field}
                                                        checked={!!field.value}
                                                        onChange={(e) => form.setFieldValue(field.name, e.target.checked)}
                                                        color="primary"
                                                    />
                                                }
                                                label="Is Trial"
                                            />
                                        )}
                                    </Field>
                                </Grid>

                                {/** Show trial quantity when trial is available */}
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="trialQuantity"
                                        label="Trial Quantity"
                                        type="number"
                                        size="small"
                                        fullWidth
                                    />
                                </Grid>

                                {/* <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="notes"
                                        label="Notes"
                                        multiline
                                        rows={3}
                                        fullWidth
                                        size="small"
                                    />
                                </Grid> */}

                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        disabled={isSubmitting || loadingData}
                                    >
                                        {isSubmitting ? 'Adding...' : 'Add Model'}
                                    </Button>
                                </Grid>
                            </Grid>

                            <Dialog open={addMfgOpen} onClose={() => setAddMfgOpen(false)} maxWidth="xs" fullWidth>
                                <AppDialogTitle title="Add Manufacturer" onClose={() => setAddMfgOpen(false)} />
                                <DialogContent>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Create a manufacturer without leaving this form.
                                    </Typography>
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        size="small"
                                        label="Manufacturer Name"
                                        value={newMfgName}
                                        onChange={(e) => setNewMfgName(e.target.value)}
                                        disabled={addMfgSubmitting}
                                    />
                                    <Button
                                        sx={{ mt: 2 }}
                                        variant="contained"
                                        onClick={async () => {
                                            if (!newMfgName.trim()) {
                                                toast.error('Please enter a manufacturer name');
                                                return;
                                            }

                                            setAddMfgSubmitting(true);
                                            try {
                                                const response = await ValidDataService.addValidManufacturer({
                                                    name: newMfgName.trim(),
                                                    type: 'contact_lens',
                                                    status: validLookup.validStatus.active,
                                                });

                                                if (response.code === 0) {
                                                    toast.success('Manufacturer added');
                                                    await fetchManufacturers();
                                                    const newId = response.data?.id || '';
                                                    if (newId) {
                                                        setFieldValue('manufacturerId', newId);
                                                    }
                                                    setNewMfgName('');
                                                    setAddMfgOpen(false);
                                                } else {
                                                    toast.error(response.message || 'Failed to add manufacturer');
                                                }
                                            } catch (error) {
                                                if (isLocalDev) {
                                                    console.error(error);
                                                }
                                                toast.error('Failed to add manufacturer');
                                            }
                                            setAddMfgSubmitting(false);
                                        }}
                                        disabled={addMfgSubmitting}
                                    >
                                        {addMfgSubmitting ? 'Saving...' : 'Save Manufacturer'}
                                    </Button>
                                </DialogContent>
                            </Dialog>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    );
}
